/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  useEffect,
  useRef,
  InputHTMLAttributes,
  useCallback,
  useState,
} from 'react';
import { useField } from '@unform/core';

import { Container } from './styles';

interface inputErrorProps {
  inputName: string;
  message: string;
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  icon?: string;
  label?: string;
  iconError?: string;
  width?: number | string;
  handleInputError?(data: inputErrorProps): void;
  messageErrorOnBlur?: string;
  isDisabled?: boolean;
  isHidden?: boolean;
  fullWidth?: boolean;
  alertMessage?: boolean;
}

const Input: React.FC<InputProps> = ({
  name,
  handleInputError,
  icon,
  label,
  iconError,
  messageErrorOnBlur,
  fullWidth,
  isDisabled,
  isHidden,
  alertMessage,
  ...rest
}) => {
  const inputRef = useRef(null);
  const [inputValueCurrent, setInputValueCurrent] = useState<string>('');

  const { fieldName, defaultValue, registerField, error, clearError } =
    useField(name);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
      setValue: (ref, value) => {
        ref.value = value;
        setSuccess(false);
        clearError();
      },
    });
  }, [fieldName, inputValueCurrent, registerField, clearError]);

  const handleBlur = useCallback(
    event => {
      if (!event.target.value) {
        if (!!handleInputError && !!messageErrorOnBlur) {
          handleInputError({
            inputName: name,
            message: messageErrorOnBlur,
          });
        }
      } else setSuccess(true);
    },
    [handleInputError, messageErrorOnBlur, name]
  );

  return (
    <Container error={!!error} success={success} fullWidth={fullWidth}>
      {isHidden ? (
        <input type="hidden" />
      ) : (
        <>
          {label ? (
            <div className="label">
              <label>{label}</label>
            </div>
          ) : (
            ''
          )}
          <div className="input-wrapper">
            {rest.required && <span className="requiredIcon">*</span>}
            {/* @ts-ignore */}
            <input
              name={name}
              ref={inputRef}
              defaultValue={defaultValue}
              disabled={isDisabled}
              onBlur={handleBlur}
              onFocus={() => {
                clearError();
                setSuccess(false);
              }}
              onChange={e => setInputValueCurrent(e.target.value)}
              onKeyPress={() => {
                clearError();
              }}
              {...rest}
            />
            {!!icon && (
              <div className="icon">
                <img src={error ? iconError || icon : icon} alt={name} />
              </div>
            )}
          </div>
          {!messageErrorOnBlur && !!error && messageErrorOnBlur && (
            <div className="error">
              <p>{error}</p>
            </div>
          )}
          {alertMessage && messageErrorOnBlur && (
            <div className="error">
              <p>{messageErrorOnBlur}</p>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default Input;
